import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '../../services/toast.service';
import { AuthService, LoginResponse, UserInfo } from '../../services/auth.service';
import { ProfileService, ForgotPasswordDto, VerifyOtpDto, ResetPasswordDto } from '../../services/profile.service';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {
  
  // Formularios
  loginForm: FormGroup;
  forgotPasswordForm: FormGroup;
  verifyOtpForm: FormGroup;
  resetPasswordForm: FormGroup;
  
  // Estados de visibilidad
  showPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;
  
  // Estados de carga
  isLoading: boolean = false;
  isSendingOtp: boolean = false;
  isVerifyingOtp: boolean = false;
  isResettingPassword: boolean = false;
  
  // Control de flujo de recuperación
  currentStep: 'login' | 'forgot-password' | 'verify-otp' | 'reset-password' = 'login';
  recoveryEmail: string = '';
  
  // Temporizador para reenvío de OTP
  otpResendCountdown: number = 0;
  private countdownInterval: any;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private profileService: ProfileService,
    private router: Router,
    private toastService: ToastService
  ) {
    this.loginForm = this.createLoginForm();
    this.forgotPasswordForm = this.createForgotPasswordForm();
    this.verifyOtpForm = this.createVerifyOtpForm();
    this.resetPasswordForm = this.createResetPasswordForm();
  }

  ngOnInit(): void {
    // Verificar si ya está autenticado
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/dashboard']);
    }
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  // ==================== CREACIÓN DE FORMULARIOS ====================

  private createLoginForm(): FormGroup {
    return this.fb.group({
      email: ['', [
        Validators.required, 
        Validators.email,
        Validators.maxLength(100)
      ]],
      password: ['', [
        Validators.required, 
        Validators.minLength(6),
        Validators.maxLength(100)
      ]]
    });
  }

  private createForgotPasswordForm(): FormGroup {
    return this.fb.group({
      email: ['', [
        Validators.required, 
        Validators.email,
        Validators.maxLength(100)
      ]]
    });
  }

  private createVerifyOtpForm(): FormGroup {
    return this.fb.group({
      otpCode: ['', [
        Validators.required,
        Validators.pattern(/^\d{6}$/),
        Validators.minLength(6),
        Validators.maxLength(6)
      ]]
    });
  }

  private createResetPasswordForm(): FormGroup {
    return this.fb.group({
      newPassword: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(100),
        LoginComponent.passwordStrengthValidator
      ]],
      confirmPassword: ['', [Validators.required]]
    }, { 
      validators: this.passwordMatchValidator 
    });
  }

  private passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');

    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    if (confirmPassword?.errors) {
      delete confirmPassword.errors['mismatch'];
      if (Object.keys(confirmPassword.errors).length === 0) {
        confirmPassword.setErrors(null);
      }
    }

    return null;
  }

  // Static validator for password strength
  static passwordStrengthValidator(control: any) {
    const value = control.value;
    if (!value) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasNonAlphanumeric = /[^a-zA-Z0-9]/.test(value);
    const isLongEnough = value.length >= 6;

    const errors: any = {};

    if (!hasUpperCase) {
      errors.missingUppercase = true;
    }
    if (!hasNonAlphanumeric) {
      errors.missingNonAlphanumeric = true;
    }
    if (!isLongEnough) {
      errors.minLength = true;
    }

    return Object.keys(errors).length === 0 ? null : errors;
  }

  // ==================== GETTERS PARA FORMULARIOS ====================

  // Login form getters
  get email() { return this.loginForm.get('email'); }
  get password() { return this.loginForm.get('password'); }

  // Forgot password form getters
  get forgotPasswordEmail() { return this.forgotPasswordForm.get('email'); }

  // Verify OTP form getters
  get otpCode() { return this.verifyOtpForm.get('otpCode'); }

  // Reset password form getters
  get newPassword() { return this.resetPasswordForm.get('newPassword'); }
  get confirmPassword() { return this.resetPasswordForm.get('confirmPassword'); }

  // Método para obtener errores de validación de un campo específico
  getFieldError(fieldName: string): string | null {
    const field = this.loginForm.get(fieldName);
    if (field && field.invalid && (field.dirty || field.touched)) {
      if (field.errors?.['required']) {
        return fieldName === 'email' ? 'El correo electrónico es requerido' : 'La contraseña es requerida';
      }
      if (field.errors?.['email']) {
        return 'Ingresa un correo electrónico válido';
      }
      if (field.errors?.['minlength']) {
        return `La contraseña debe tener al menos ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors?.['maxlength']) {
        return `El campo debe tener máximo ${field.errors['maxlength'].requiredLength} caracteres`;
      }
    }
    return null;
  }

  // Método para verificar si un campo tiene errores
  hasFieldError(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onSubmit(): void {
    console.log(environment.apiUrl)
    // Marcar todos los campos como touched para mostrar errores de validación
    this.loginForm.markAllAsTouched();

    if (this.loginForm.invalid) {
      this.toastService.showToast('error', 'Por favor, corrige los errores en el formulario');
      return;
    }

    this.isLoading = true;

    const loginData = this.loginForm.value;

    this.authService.login(loginData).subscribe({
      next: (response: LoginResponse) => {
        console.log('Login exitoso, obteniendo información del usuario...');
        
        // Después del login exitoso, obtener información del usuario
        this.authService.fetchCurrentUser().subscribe({
          next: (userInfo: UserInfo) => {
            this.isLoading = false;
            console.log('Información del usuario obtenida:', userInfo);
            this.toastService.showToast('success', `¡Bienvenido ${userInfo.firstName}! Inicio de sesión exitoso`);
            this.router.navigate(['/dashboard']);
          },
          error: (userError: HttpErrorResponse) => {
            // El login fue exitoso pero falló la obtención de datos del usuario
            // Aún así permitir el acceso
            this.isLoading = false;
            console.warn('Login exitoso pero no se pudo obtener información del usuario:', userError);
            this.toastService.showToast('success', '¡Bienvenido! Inicio de sesión exitoso');
            this.router.navigate(['/dashboard']);
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        console.error('Error en login:', error);
        
        let errorMessage = 'Error al iniciar sesión';
        if (error.status === 401) {
          errorMessage = 'Credenciales incorrectas. Verifica tu email y contraseña';
        } else if (error.status === 400) {
          errorMessage = 'Datos de acceso inválidos';
        } else if (error.status === 0) {
          errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión';
        }
        
        this.toastService.showToast('error', errorMessage);
        
        // Resetear la contraseña por seguridad
        this.loginForm.patchValue({ password: '' });
      }
    });
  }

  // ==================== MÉTODOS DE RECUPERACIÓN DE CONTRASEÑA ====================

  /**
   * Cambia al paso de recuperación de contraseña
   */
  goToForgotPassword(): void {
    this.currentStep = 'forgot-password';
    this.forgotPasswordForm.reset();
    
    // Si hay un email en el formulario de login, pre-llenarlo
    const loginEmail = this.email?.value;
    if (loginEmail) {
      this.forgotPasswordForm.patchValue({ email: loginEmail });
    }
  }

  /**
   * Regresa al formulario de login
   */
  backToLogin(): void {
    this.currentStep = 'login';
    this.clearCountdown();
    this.recoveryEmail = '';
  }

  /**
   * Envía el código OTP al email
   */
  onSubmitForgotPassword(): void {
    this.forgotPasswordForm.markAllAsTouched();

    if (this.forgotPasswordForm.invalid) {
      this.toastService.showToast('error', 'Por favor, ingresa un email válido');
      return;
    }

    const forgotPasswordDto: ForgotPasswordDto = {
      email: this.forgotPasswordEmail?.value
    };

    this.isSendingOtp = true;

    this.profileService.forgotPassword(forgotPasswordDto).subscribe({
      next: (response) => {
        this.isSendingOtp = false;
        this.recoveryEmail = forgotPasswordDto.email;
        this.currentStep = 'verify-otp';
        this.startOtpCountdown();
        this.toastService.showToast('success', response.message);
      },
      error: (error: Error) => {
        this.isSendingOtp = false;
        this.toastService.showToast('error', error.message);
      }
    });
  }

  /**
   * Verifica el código OTP
   */
  onSubmitVerifyOtp(): void {
    this.verifyOtpForm.markAllAsTouched();

    if (this.verifyOtpForm.invalid) {
      this.toastService.showToast('error', 'Por favor, ingresa un código válido de 6 dígitos');
      return;
    }

    const verifyOtpDto: VerifyOtpDto = {
      email: this.recoveryEmail,
      otpCode: this.otpCode?.value
    };

    this.isVerifyingOtp = true;

    this.profileService.verifyOtp(verifyOtpDto).subscribe({
      next: (response) => {
        this.isVerifyingOtp = false;
        this.currentStep = 'reset-password';
        this.clearCountdown();
        this.toastService.showToast('success', response.message);
      },
      error: (error: Error) => {
        this.isVerifyingOtp = false;
        this.toastService.showToast('error', error.message);
      }
    });
  }

  /**
   * Resetea la contraseña
   */
  onSubmitResetPassword(): void {
    this.resetPasswordForm.markAllAsTouched();

    if (this.resetPasswordForm.invalid) {
      this.toastService.showToast('error', 'Por favor, corrige los errores en el formulario');
      return;
    }

    const resetPasswordDto: ResetPasswordDto = {
      email: this.recoveryEmail,
      otpCode: this.otpCode?.value,
      newPassword: this.newPassword?.value,
      confirmPassword: this.confirmPassword?.value
    };

    this.isResettingPassword = true;

    this.profileService.resetPassword(resetPasswordDto).subscribe({
      next: (response) => {
        this.isResettingPassword = false;
        this.toastService.showToast('success', response.message);
        this.currentStep = 'login';
        this.resetAllForms();
      },
      error: (error: Error) => {
        this.isResettingPassword = false;
        this.toastService.showToast('error', error.message);
      }
    });
  }

  /**
   * Reenvía el código OTP
   */
  resendOtp(): void {
    if (this.otpResendCountdown > 0) {
      return;
    }

    const forgotPasswordDto: ForgotPasswordDto = {
      email: this.recoveryEmail
    };

    this.isSendingOtp = true;

    this.profileService.forgotPassword(forgotPasswordDto).subscribe({
      next: (response) => {
        this.isSendingOtp = false;
        this.startOtpCountdown();
        this.toastService.showToast('success', 'Código reenviado exitosamente');
      },
      error: (error: Error) => {
        this.isSendingOtp = false;
        this.toastService.showToast('error', error.message);
      }
    });
  }

  // ==================== MÉTODOS UTILITARIOS ====================

  /**
   * Inicia el countdown para reenvío de OTP
   */
  private startOtpCountdown(): void {
    this.otpResendCountdown = 60; // 60 segundos
    this.countdownInterval = setInterval(() => {
      this.otpResendCountdown--;
      if (this.otpResendCountdown <= 0) {
        this.clearCountdown();
      }
    }, 1000);
  }

  /**
   * Limpia el countdown
   */
  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.otpResendCountdown = 0;
  }

  /**
   * Resetea todos los formularios de recuperación
   */
  private resetAllForms(): void {
    this.forgotPasswordForm.reset();
    this.verifyOtpForm.reset();
    this.resetPasswordForm.reset();
    this.recoveryEmail = '';
    this.clearCountdown();
  }

  /**
   * Obtiene errores específicos para formularios de recuperación
   */
  getRecoveryFieldError(fieldName: string, formType: 'forgot' | 'otp' | 'reset'): string | null {
    let form: FormGroup;
    
    switch (formType) {
      case 'forgot':
        form = this.forgotPasswordForm;
        break;
      case 'otp':
        form = this.verifyOtpForm;
        break;
      case 'reset':
        form = this.resetPasswordForm;
        break;
      default:
        return null;
    }

    const field = form.get(fieldName);
    if (field && field.invalid && (field.dirty || field.touched)) {
      if (field.errors?.['required']) {
        return this.getRecoveryRequiredMessage(fieldName);
      }
      if (field.errors?.['email']) {
        return 'Ingresa un correo electrónico válido';
      }
      if (field.errors?.['pattern']) {
        return 'El código debe contener exactamente 6 dígitos';
      }
      if (field.errors?.['minlength'] || field.errors?.['maxlength']) {
        return fieldName === 'otpCode' 
          ? 'El código debe tener exactamente 6 dígitos'
          : 'La contraseña debe tener al menos 6 caracteres';
      }
      if (field.errors?.['mismatch']) {
        return 'Las contraseñas no coinciden';
      }
      if (field.errors?.['missingUppercase']) {
        return 'La contraseña debe contener al menos una letra mayúscula (A-Z)';
      }
      if (field.errors?.['missingNonAlphanumeric']) {
        return 'La contraseña debe contener al menos un carácter especial (!@#$%^&*)';
      }
      if (field.errors?.['minLength'] && fieldName !== 'otpCode') {
        return 'La contraseña debe tener al menos 6 caracteres';
      }
    }
    
    return null;
  }

  private getRecoveryRequiredMessage(fieldName: string): string {
    const messages: { [key: string]: string } = {
      email: 'El correo electrónico es requerido',
      otpCode: 'El código de verificación es requerido',
      newPassword: 'La nueva contraseña es requerida',
      confirmPassword: 'La confirmación de contraseña es requerida'
    };
    
    return messages[fieldName] || 'Este campo es requerido';
  }

  hasRecoveryFieldError(fieldName: string, formType: 'forgot' | 'otp' | 'reset'): boolean {
    let form: FormGroup;
    
    switch (formType) {
      case 'forgot':
        form = this.forgotPasswordForm;
        break;
      case 'otp':
        form = this.verifyOtpForm;
        break;
      case 'reset':
        form = this.resetPasswordForm;
        break;
      default:
        return false;
    }

    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  // Método para resetear el formulario
  resetForm(): void {
    this.loginForm.reset();
    this.showPassword = false;
  }

  // ==================== CONTROL DE VISIBILIDAD DE CONTRASEÑAS ====================

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
